import type { BuilderLocationSettings, LocationType } from "api-types/builders/builderTypes";
import { getLocationDisplayInfo } from "api-types/locations/locationDisplay";
import lazySuspense from "ui/hooks/lazySuspense";
import IconBuildingApartment from "ui/icons/IconBuildingApartment";
import IconCommunity from "ui/icons/IconCommunity";
import IconCompany from "ui/icons/IconCompany";
import IconDivision from "ui/icons/IconDivision";
import IconGlobeHemisphereWest from "ui/icons/IconGlobeHemisphereWest";
import IconPhase from "ui/icons/IconPhase";
import IconRegion from "ui/icons/IconRegion";
import type { SvgIconProps } from "ui/icons/SVGIcon";

/**
 * Static icon registry - commonly used default icons loaded immediately
 * Note: "multiunit" is included as an alias so callers can pass the location type directly
 */
const staticIcons = {
  multiunit: IconBuildingApartment,
  community: IconCommunity,
  company: IconCompany,
  division: IconDivision,
  locations: IconGlobeHemisphereWest,
  phase: IconPhase,
  region: IconRegion,
} as const;

/**
 * Dynamic icon registry - decorative icons are code-split and loaded on demand
 */
const dynamicIcons = {
  barricade: lazySuspense(() => import("ui/icons/IconBarricade")),
  briefcaseSimple: lazySuspense(() => import("ui/icons/IconBriefcaseSimple")),
  buildingApartment: lazySuspense(() => import("ui/icons/IconBuildingApartment")),
  buildings: lazySuspense(() => import("ui/icons/IconBuildings")),
  bulldozer: lazySuspense(() => import("ui/icons/IconBulldozer")),
  compass: lazySuspense(() => import("ui/icons/IconCompass")),
  crane: lazySuspense(() => import("ui/icons/IconCrane")),
  globeSimple: lazySuspense(() => import("ui/icons/IconGlobeSimple")),
  hardHat: lazySuspense(() => import("ui/icons/IconHardHat")),
  house: lazySuspense(() => import("ui/icons/IconHouse")),
  houseFront2: lazySuspense(() => import("ui/icons/IconHouseFront2")),
  ladder: lazySuspense(() => import("ui/icons/IconLadder")),
  map: lazySuspense(() => import("ui/icons/IconMap")),
  mapPinArea: lazySuspense(() => import("ui/icons/IconMapPinArea")),
  mapPinSimple: lazySuspense(() => import("ui/icons/IconMapPinSimple")),
  mapPinSimpleArea: lazySuspense(() => import("ui/icons/IconMapPinSimpleArea")),
  mountains: lazySuspense(() => import("ui/icons/IconMountains")),
  network: lazySuspense(() => import("ui/icons/IconNetwork")),
  paintBrushHousehold: lazySuspense(() => import("ui/icons/IconPaintBrushHousehold")),
  pipeWrench: lazySuspense(() => import("ui/icons/IconPipeWrench")),
  storefront: lazySuspense(() => import("ui/icons/IconStorefront")),
  tractor: lazySuspense(() => import("ui/icons/IconTractor")),
  truck: lazySuspense(() => import("ui/icons/IconTruck")),
  usersFour: lazySuspense(() => import("ui/icons/IconUsersFour")),
  warehouse: lazySuspense(() => import("ui/icons/IconWarehouse")),
} as const;

type StaticIconName = keyof typeof staticIcons;
type DynamicIconName = keyof typeof dynamicIcons;
export type LocationIconName = StaticIconName | DynamicIconName;

/**
 * Type guards to validate icon names at runtime
 */
function isStaticIconName(value: string): value is StaticIconName {
  return value in staticIcons;
}

function isDynamicIconName(value: string): value is DynamicIconName {
  return value in dynamicIcons;
}

/**
 * Get an icon component for a location by icon name.
 * Falls back to IconGlobeHemisphereWest if the icon name is not found.
 * Default icons load immediately; decorative icons are code-split via Next.js dynamic imports.
 */
export function getLocationIcon(iconName?: string, iconProps: SvgIconProps = {}) {
  // Check static icons first (instant render)
  if (iconName && isStaticIconName(iconName)) {
    const IconComponent = staticIcons[iconName];
    return <IconComponent {...iconProps} />;
  }

  // Check dynamic icons (lazy loaded)
  if (iconName && isDynamicIconName(iconName)) {
    const IconComponent = dynamicIcons[iconName];
    return <IconComponent {...iconProps} />;
  }

  // Fallback for unknown or undefined icons
  return <IconGlobeHemisphereWest {...iconProps} />;
}

/**
 * Get icon component for a location type, respecting builder customization.
 * This is the preferred method when builder settings are available.
 */
export function getLocationIconForType(
  locationType: LocationType | undefined,
  builderSettings?: BuilderLocationSettings | null,
  iconProps: SvgIconProps = {},
) {
  if (!locationType) {
    return getLocationIcon(undefined, iconProps);
  }
  const { icon } = getLocationDisplayInfo(locationType, builderSettings);
  return getLocationIcon(icon, iconProps);
}
